import React, { useState, useEffect } from "react";
import { useBeforeInstallPromptState } from "@/zustand/BeforeInstallPromptState";
import {
  IS_SAFARI,
  IS_CHROME_ON_IOS,
  IS_IOS_INSTALLABLE,
} from "@/lib/environmentDetector";
import { useTranslation } from "react-i18next";
import BrowserNotSupportedInstructions from "./BrowserNotSupportedInstructions";
import SafariInstructions from "./SafariInstructions";
import ChromeInstructions from "./ChromeInstructions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/primitives/dialog";
import { Button } from "@/components/primitives/button";
import { safeStorage } from "@/utils/safeStorage";
import { useAnnouncementSlot } from "@/zustand/AnnouncementState";
import useAuthSession from "@/middleware/hooks/useAuthSession";

// Check if the dialog was dismissed within the last 24 hours
const STORAGE_KEY = "installAppDialogDismissed";

interface InstallAppDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

// Main InstallAppDialog component
const InstallAppDialog: React.FC<InstallAppDialogProps> = ({
  open: externalOpen,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const { isInstallable, promptInstall } = useBeforeInstallPromptState();
  // Pre-login (login/register pages) the install prompt keeps its original
  // behavior — it's the only dialog there and matters for onboarding. Once
  // logged in it competes for the single per-boot announcement slot.
  const { isLoggedIn } = useAuthSession();
  // Externally-controlled instances (AccountMenu's manual "Install app"
  // entry passes `open`) are explicit user actions, not announcements —
  // they must never be suppressed by the queue (dual-review HIGH).
  const isExternallyControlled = externalOpen !== undefined;
  const hasAnnouncementSlot = useAnnouncementSlot(
    "install-app",
    isLoggedIn && !isExternallyControlled,
  );
  const [internalOpen, setInternalOpen] = useState(true);
  // Add loading state to prevent flash on refresh
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const dismissedTime = safeStorage.get(STORAGE_KEY);
    if (dismissedTime) {
      const dismissedAt = parseInt(dismissedTime, 10);
      const currentTime = Date.now();
      const hoursPassed = (currentTime - dismissedAt) / (1000 * 60 * 60);

      // If less than 24 hours have passed, close the dialog
      if (hoursPassed < 24) {
        setInternalOpen(false);
      }
    }
    // Mark loading as complete after checking localStorage
    setIsLoading(false);
  }, []);

  // Use external state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;

  const setOpen = (value: boolean) => {
    setInternalOpen(value);
    if (onOpenChange) {
      onOpenChange(value);
    }
  };
  const shouldShowDialog =
    (isInstallable || IS_IOS_INSTALLABLE) &&
    (isExternallyControlled || !isLoggedIn || hasAnnouncementSlot);

  // Don't render anything while loading or if dialog shouldn't be shown
  if (isLoading || !shouldShowDialog) {
    return null;
  }

  const handleInstall = () => {
    if (isInstallable) {
      promptInstall();
    }
  };

  const handleDismiss = () => {
    // Store the current timestamp in localStorage
    safeStorage.set(STORAGE_KEY, Date.now().toString());
    setOpen(false);
  };

  // i18next-http-backend loads translations asynchronously; before they
  // resolve, t(key, { returnObjects: true }) returns the key STRING rather
  // than the array. Guard so a non-array result renders an empty list instead
  // of crashing with ".map is not a function" (see issue #934).
  const rawBenefits = t("installAppIOS.benefits", { returnObjects: true });
  const benefits = Array.isArray(rawBenefits) ? (rawBenefits as string[]) : [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold text-foreground">
            {t("installApp")}
          </DialogTitle>
          <DialogDescription className="text-center text-foreground/70">
            {t("installAppDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 flex items-center gap-4">
          <img src="/logo/192.png" alt="ORION" className="size-16 rounded-lg" />
          <div>
            <h3 className="font-semibold text-foreground">{t("appName")}</h3>
            <p className="text-sm text-muted-foreground">
              {t("appDescription")}
            </p>
          </div>
        </div>

        {(() => {
          // Using a switch(true) pattern for better maintainability
          // and to prioritize cases in a specific order
          switch (true) {
            // Case 1: Standard install prompt available (Chrome/Edge on desktop/Android)
            case isInstallable:
              return (
                <button
                  onClick={handleInstall}
                  className="w-full rounded-lg bg-primary py-3 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  {t("installApp")}
                </button>
              );

            // Case 2: Chrome on iOS
            case IS_CHROME_ON_IOS:
              return (
                <div className="my-4 rounded-lg border-2 border-primary/40 bg-primary/5 p-5 shadow-sm">
                  <ChromeInstructions />
                </div>
              );

            // Case 3: Safari on iOS
            case IS_SAFARI:
              return (
                <div className="my-4 rounded-lg border-2 border-primary/40 bg-primary/5 p-5 shadow-sm">
                  <SafariInstructions />
                </div>
              );

            // Default: Unsupported browser on any platform
            default:
              return (
                <div className="my-4 rounded-lg border-2 border-primary/40 bg-primary/5 p-5 shadow-sm">
                  <BrowserNotSupportedInstructions />
                </div>
              );
          }
        })()}

        <ul className="list-disc space-y-1 pl-5 text-foreground/70">
          {benefits.map((benefit, idx) => (
            <li key={idx}>{benefit}</li>
          ))}
        </ul>

        <DialogFooter className="flex justify-center sm:justify-center">
          <Button variant="outline" onClick={handleDismiss}>
            {t("continueOnWebsite")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InstallAppDialog;
